import type { JSX } from 'react';
import { useId, useState } from 'react';

import { useNotifyExecutiveActionRequiredToolRule } from 'features/notification-config';
import { AlertTriangle, Loader2, MessageSquare } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

import ControlSettingCard from './ControlSettingCard';

interface SuccessFeedbackState {
  title: string;
  message: string;
}

interface ControlPanelSectionProps {
  masterProjectIdForBusinessRules: number;
  businessRuleDisplayName: string;
}

const ControlPanelSection = ({
  masterProjectIdForBusinessRules,
  businessRuleDisplayName,
}: ControlPanelSectionProps): JSX.Element => {
  const inChatHeadingId = useId();
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [successFeedback, setSuccessFeedback] = useState<SuccessFeedbackState | null>(null);

  const { isToolEnabled, isRuleQueryLoading, isQueryError, queryError, isSaving, setToolEnabled, refetch } =
    useNotifyExecutiveActionRequiredToolRule(masterProjectIdForBusinessRules, businessRuleDisplayName);

  const handleInChatToggle = async (next: boolean): Promise<void> => {
    setToggleError(null);
    setSuccessFeedback(null);
    try {
      const action = await setToolEnabled(next);
      if (action === 'created') {
        setSuccessFeedback({
          title: 'Regla creada',
          message: `La business rule se guardó con el título «${businessRuleDisplayName}». Notificaciones in-chat activadas.`,
        });
        return;
      }
      if (action === 'set-false') {
        setSuccessFeedback({
          title: 'Regla actualizada',
          message:
            'La business rule NOTIFY_EXECUTIVE_ACTION_REQUIRED_TOOL_ENABLED quedó en falso. Notificaciones in-chat ' +
            'desactivadas.',
        });
        return;
      }
      if (action === 'set-true') {
        setSuccessFeedback({
          title: 'Regla actualizada',
          message:
            'La business rule NOTIFY_EXECUTIVE_ACTION_REQUIRED_TOOL_ENABLED quedó en verdadero. Notificaciones ' +
            'in-chat activadas.',
        });
      }
    } catch (error: unknown) {
      setToggleError(extractErrorMessage(error, 'No se pudo actualizar la configuración de notificaciones in-chat.'));
    }
  };

  const loadRuleErrorMessage = isQueryError
    ? extractErrorMessage(queryError, 'No se pudo cargar la regla de negocio para este proyecto.')
    : null;

  return (
    <div className="flex flex-col gap-6">
      {successFeedback ? (
        <Alert role="status" className="border-success/40 bg-success-light text-success font-body">
          <AlertTitle className="font-semibold text-success">{successFeedback.title}</AlertTitle>
          <AlertDescription className="mt-1 text-success">{successFeedback.message}</AlertDescription>
          <AlertAction>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setSuccessFeedback(null)}
              aria-label="Cerrar aviso"
            >
              ×
            </Button>
          </AlertAction>
        </Alert>
      ) : null}

      {loadRuleErrorMessage ? (
        <Alert className="border-error/40 bg-error-light text-error font-body">
          <AlertDescription>{loadRuleErrorMessage}</AlertDescription>
          <AlertAction>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                refetch().catch(() => {});
              }}
            >
              Reintentar
            </Button>
          </AlertAction>
        </Alert>
      ) : null}

      <Alert className="border-warning bg-warning-light font-body">
        <AlertTriangle className="size-5 shrink-0 text-warning" aria-hidden />
        <AlertDescription className="text-neutral-900 leading-relaxed">
          <span className="font-bold">Nota importante:</span> cualquier cambio aquí afectará a todos los usuarios de
          forma inmediata. Asegúrate de coordinar antes de desactivar notificaciones relevantes para el equipo.
        </AlertDescription>
      </Alert>

      <ControlSettingCard
        titleId={inChatHeadingId}
        title="Aviso automático a ejecutivo/a"
        description="Permite que la IA avise cuando detecta que un cliente necesita atención humana."
        icon={<MessageSquare className="size-5 shrink-0" aria-hidden />}
        checked={isToolEnabled}
        onCheckedChange={(next) => {
          void handleInChatToggle(next);
        }}
        tooltip="Esta opción controla la business rule NOTIFY_EXECUTIVE_ACTION_REQUIRED_TOOL_ENABLED y habilita la tool interna notifyExecutiveActionRequired. Si está desactivada, la IA no puede usar esa acción."
        switchDisabled={isRuleQueryLoading || isSaving || isQueryError}
        switchSlot={
          isRuleQueryLoading ? (
            <div className="flex items-center gap-2 text-sm text-text-muted font-body" aria-busy="true">
              <Loader2 className="size-6 shrink-0 animate-spin" aria-hidden />
              <span className="sr-only">Cargando estado de la regla</span>
            </div>
          ) : undefined
        }
      />

      {toggleError ? (
        <Alert role="status" className="border-error/40 bg-error-light text-error font-body">
          <AlertDescription>{toggleError}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};

export default ControlPanelSection;
